import type { DocumentResult } from '../shared/desktop-api';

/** The part of a document revision that decides which scalar fields may be saved. */
export const documentFieldContractToken = (document: DocumentResult) =>
  JSON.stringify([
    document.documentType,
    document.documentTypeId ?? null,
    document.documentTypeSchemaVersion ?? null,
    document.fieldContractSignature ?? null,
  ]);

/** Wait until every save already admitted to a serialized lane has settled. */
export const waitForPendingSaveLane = async (
  pending: () => number,
  currentLane: () => Promise<unknown>,
  failed: () => boolean = () => false,
) => {
  // A save can enqueue another save before the promise we captured settles.
  // Re-read both facts after every await; one snapshot is not a drain.
  let observedThisLane = false;
  while (pending() > 0) {
    observedThisLane = true;
    await currentLane();
  }
  // A failure receipt belongs to the lane which was actually drained. Once no
  // save was pending at admission it is historical state, not a permanent veto
  // on every later confirmation and review check in the batch.
  return !observedThisLane || !failed();
};

export type LineItemSaveKind = 'line-item-correction' | 'line-item-added' | 'line-item-deleted';

/** A queued checkmark vouches for the row the operator saw, not a later revision. */
export const assertLineItemConfirmationCurrent = <
  T extends { rowNumber: number; verified?: boolean },
>(
  durable: T,
  before: T,
  after: T,
): void => {
  if (before.verified || !after.verified) return;
  const keys = new Set([...Object.keys(durable), ...Object.keys(before)]);
  keys.delete('verified');
  keys.delete('arithmeticValidated');
  // Who typed the row is not one of its values: the saved reply carries it before the paint does.
  keys.delete('operatorTyped');
  if (
    [...keys].some(
      (key) => JSON.stringify(durable[key as keyof T]) !== JSON.stringify(before[key as keyof T]),
    )
  )
    throw new Error(
      'This line changed before its confirmation was saved. Review the current line and confirm it again.',
    );
};

/**
 * Apply one recorded row mutation to the last engine-accepted rows.
 *
 * The renderer can paint several edits before their serialized saves finish.
 * Reusing that painted document for the next native call lets a rejected first
 * edit hitchhike inside the second call without its own correction event. Each
 * call is therefore projected from the durable watermark instead.
 */
export const applyLineItemSave = <T extends { rowNumber: number }>(
  rows: readonly T[],
  kind: LineItemSaveKind,
  before: T | null,
  after: T | null,
): T[] => {
  if (kind === 'line-item-added') {
    if (!after || rows.some((row) => row.rowNumber === after.rowNumber))
      throw new Error('The added line item no longer has one unused row number.');
    return [...rows, after];
  }
  if (!before) throw new Error('The saved line item no longer has its earlier row identity.');
  const matching = rows.filter((row) => row.rowNumber === before.rowNumber);
  if (matching.length !== 1)
    throw new Error('The saved line item no longer identifies exactly one durable row.');
  if (kind === 'line-item-deleted')
    return rows
      .filter((row) => row.rowNumber !== before.rowNumber)
      .map((row, index) => ({ ...row, rowNumber: index + 1 }));
  if (!after) throw new Error('The corrected line item has no replacement value.');
  // `after` is the edit as it looked when admitted, not a new whole-row
  // authority. Two quick edits to different cells can be queued from the same
  // visible row; replacing with the second stale object would put the first cell
  // back. Project only properties this gesture actually changed onto the durable
  // row returned by the preceding save.
  // arithmeticValidated is a verdict over all three numeric cells, not an input
  // cell. A stale gesture may have derived it from an older row revision; the
  // caller recomputes that verdict after this delta lands on the durable row.
  const changed = Object.keys(after).filter(
    (key) =>
      key !== 'rowNumber' &&
      key !== 'arithmeticValidated' &&
      !Object.is(before[key as keyof T], after[key as keyof T]),
  );
  return rows.map((row) => {
    if (row.rowNumber !== before.rowNumber) return row;
    const patch = Object.fromEntries(changed.map((key) => [key, after[key as keyof T]]));
    return { ...row, ...patch, rowNumber: row.rowNumber };
  });
};
